// Parser de nombres y rutas — Dashboard JMA AFAC
//
// Implementa el contrato de la skill `jma-afac-data-mapper`:
//   - parseDocumentName(filename, relativePath) → metadata estructurada
//   - baseNameSinVersion(filename)             → nombre sin versión
//   - compararVersiones(a, b)                  → orden numérico
//   - detectarObsoletasConviviendo(docs)       → conviven en misma carpeta
//   - detectarDesactualizadosCliente(cli, plt) → instancia vs plantilla
//   - agregarPorCliente(docs, clienteId)       → resumen por fase/sección
//
// Todas las funciones son puras (sin estado externo, sin efectos).

// ---------------------------------------------------------------------------
// Patrones reutilizables
// ---------------------------------------------------------------------------

const EXT_PATTERN = /\.(docx|xlsx|pptx|pdf)$/i
const VERSION_PATTERN = /_v(\d+)\.(\d+)$/i
const ENTREGABLE_PATTERN = /(?:^|_)(E-?\d{1,3})(?=_|$)/i
const FASE_PATTERN_NAME = /(?:^|_)F([0-6])(?=_|$)/i
const FASE_PATTERN_PATH = /(?:^|\/)Fase_([0-6])_[^/]+/i
const SECCION_PATTERN_PATH = /(?:^|\/)Seccion_(\d{2})_[^/]+/i
const CLIENTE_PATH_PATTERN = /(?:^|\/)03_Clientes\/Cliente_([^/]+)/i
const PLANTILLA_PATH_PATTERN = /(?:^|\/)02_Plantillas_Maestras\//i

// Palabras que jamás son código de cliente aunque encajen en mayúsculas cortas.
const PALABRAS_RESERVADAS = new Set([
  'JMA', 'AFAC', 'TR', 'BN', 'PDF', 'OK', 'NA', 'TBD', 'PYME', 'PROD'
])

// Mapeo carpeta → audiencia documental.
const AUDIENCIAS_POR_CARPETA = [
  ['/00_Checklist/',   'Internos JMA'],
  ['/01_Internos_JMA/', 'Internos JMA'],
  ['/02_Cliente/',     'Cliente'],
  ['/03_Socios/',      'Socios'],
  ['/Carpeta_AFAC/',   'Carpeta AFAC'],
  ['/Comerciales/',    'Cliente'],
  ['/Transversales/',  'Transversal']
]

// ---------------------------------------------------------------------------
// Helpers internos
// ---------------------------------------------------------------------------

function normalizePath(path) {
  return String(path || '').replace(/\\/g, '/')
}

function isClientToken(token) {
  if (!/^[A-Z0-9]{2,6}$/.test(token)) return false
  if (PALABRAS_RESERVADAS.has(token)) return false
  if (/^F[0-6]$/.test(token)) return false
  if (/^E-?\d+$/.test(token)) return false
  // Versiones tipo V1, V2... también descartadas como cliente.
  if (/^V\d+$/.test(token)) return false
  return true
}

function findAudienciaInPath(path) {
  const wrapped = path.startsWith('/') ? path : `/${path}`
  for (const [needle, audiencia] of AUDIENCIAS_POR_CARPETA) {
    if (wrapped.includes(needle)) return audiencia
  }
  return null
}

function deriveTipo(workName, { fase, codigoEntregable, cliente, clienteEnRuta }) {
  let tipo = String(workName || '').replace(/^JMA[_-]?AFAC[_-]?/i, '')
  if (fase) {
    const faseNum = fase.match(/\d/)?.[0]
    if (faseNum) tipo = tipo.replace(new RegExp(`(?:^|_)F${faseNum}(?=_|$)`, 'i'), '_')
  }
  if (codigoEntregable) {
    const cleaned = codigoEntregable.replace('E-', 'E-?')
    tipo = tipo.replace(new RegExp(`(?:^|_)${cleaned}(?=_|$)`, 'i'), '_')
  }
  if (cliente && !clienteEnRuta) {
    tipo = tipo.replace(new RegExp(`(?:^|_)${cliente}(?=_|$)`), '_')
  }
  return tipo
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .replace(/_/g, ' ')
    .trim()
}

// ---------------------------------------------------------------------------
// API pública
// ---------------------------------------------------------------------------

/**
 * Extrae metadata estructurada de un archivo a partir de su nombre y de la
 * ruta relativa (desde la raíz `Documentación para Consultoria/`).
 *
 * @param {string} filename Ej.: "JMA_AFAC_Plan_Pentest_Diagnostico_F1_E-10_GWTC_v1.1.docx"
 * @param {string} [relativePath=''] Ej.: "03_Clientes/Cliente_GWTC/Fase_1.../02_Cliente/JMA_AFAC_..."
 */
export function parseDocumentName(filename, relativePath = '') {
  const path = normalizePath(relativePath)
  const file = String(filename || '')
  const nombreSinExt = file.replace(EXT_PATTERN, '')

  // Versión (siempre al final del nombre, antes de la extensión).
  let version = null
  let workName = nombreSinExt
  const versionMatch = workName.match(VERSION_PATTERN)
  if (versionMatch) {
    version = `${versionMatch[1]}.${versionMatch[2]}`
    workName = workName.replace(VERSION_PATTERN, '')
  }

  // Código entregable (E-01, E-10, etc.).
  let codigoEntregable = null
  const entregableMatch = workName.match(ENTREGABLE_PATTERN)
  if (entregableMatch) {
    const raw = entregableMatch[1].toUpperCase()
    codigoEntregable = raw.startsWith('E-') ? raw : raw.replace(/^E/, 'E-')
  }

  // Fase: la ruta tiene prioridad sobre el nombre.
  let fase = null
  const fasePath = path.match(FASE_PATTERN_PATH)
  if (fasePath) {
    fase = `Fase ${fasePath[1]}`
  } else {
    const faseName = workName.match(FASE_PATTERN_NAME)
    if (faseName) fase = `Fase ${faseName[1]}`
  }

  // Audiencia derivada de la ruta.
  const audiencia = findAudienciaInPath(path)

  // Sección de la Carpeta AFAC (01-13).
  let seccionAFAC = null
  const seccionMatch = path.match(SECCION_PATTERN_PATH)
  if (seccionMatch) seccionAFAC = parseInt(seccionMatch[1], 10)

  // Cliente: ruta tiene prioridad; fallback a sufijo en mayúsculas en el nombre.
  let cliente = null
  let clienteEnRuta = false
  const clientePath = path.match(CLIENTE_PATH_PATTERN)
  if (clientePath) {
    cliente = clientePath[1]
    clienteEnRuta = true
  } else {
    const tokens = workName.split('_').filter(Boolean)
    for (let i = tokens.length - 1; i >= 0; i--) {
      if (isClientToken(tokens[i])) {
        cliente = tokens[i]
        break
      }
    }
  }

  const esPlantilla = PLANTILLA_PATH_PATTERN.test(path) && !clienteEnRuta

  const tipo = deriveTipo(workName, {
    fase,
    codigoEntregable,
    cliente,
    clienteEnRuta
  })

  return {
    filename: file,
    nombreSinExt,
    tipo: tipo || nombreSinExt,
    fase,
    audiencia,
    cliente,
    esPlantilla,
    codigoEntregable,
    version,
    seccionAFAC
  }
}

/**
 * Devuelve el nombre sin extensión y sin sufijo de versión.
 * Útil para agrupar versiones del mismo documento.
 */
export function baseNameSinVersion(filename) {
  return String(filename || '')
    .replace(EXT_PATTERN, '')
    .replace(VERSION_PATTERN, '')
}

/**
 * Compara dos versiones tipo "1.2" / "1.10".
 * - Sin versión queda al final (se considera "menor").
 */
export function compararVersiones(a, b) {
  if (!a && !b) return 0
  if (!a) return -1
  if (!b) return 1
  const [aMaj, aMin] = String(a).split('.').map((n) => parseInt(n, 10) || 0)
  const [bMaj, bMin] = String(b).split('.').map((n) => parseInt(n, 10) || 0)
  if (aMaj !== bMaj) return aMaj - bMaj
  return aMin - bMin
}

/**
 * Recibe una lista de documentos (con `name`, `relativePath`, `version`)
 * y devuelve aquellos que conviven con una versión más reciente en la
 * misma carpeta.
 */
export function detectarObsoletasConviviendo(documentos) {
  const grupos = new Map()
  for (const doc of documentos) {
    const rel = normalizePath(doc.relativePath || doc.name || '')
    const folder = rel.includes('/') ? rel.slice(0, rel.lastIndexOf('/')) : ''
    const base = baseNameSinVersion(doc.name || rel.split('/').pop())
    const key = `${folder}::${base}`
    if (!grupos.has(key)) grupos.set(key, [])
    grupos.get(key).push(doc)
  }
  const obsoletas = []
  for (const grupo of grupos.values()) {
    if (grupo.length <= 1) continue
    grupo.sort((a, b) => compararVersiones(b.version, a.version))
    for (let i = 1; i < grupo.length; i++) obsoletas.push(grupo[i])
  }
  return obsoletas
}

/**
 * Identifica instancias cliente cuya plantilla maestra tiene versión mayor.
 * Devuelve pares { instancia, plantilla } ordenados como vienen.
 */
export function detectarDesactualizadosCliente(documentosCliente, documentosPlantilla) {
  const plantillasPorCodigo = new Map()
  for (const p of documentosPlantilla) {
    const codigo = p.codigoEntregable || p?.metadata?.codigoEntregable
    if (!codigo) continue
    const existing = plantillasPorCodigo.get(codigo)
    const pv = p.version || p?.metadata?.version
    const ev = existing?.version || existing?.metadata?.version
    if (!existing || compararVersiones(pv, ev) > 0) {
      plantillasPorCodigo.set(codigo, p)
    }
  }
  const desactualizados = []
  for (const doc of documentosCliente) {
    const codigo = doc.codigoEntregable || doc?.metadata?.codigoEntregable
    if (!codigo) continue
    const plantilla = plantillasPorCodigo.get(codigo)
    if (!plantilla) continue
    const dv = doc.version || doc?.metadata?.version
    const pv = plantilla.version || plantilla?.metadata?.version
    if (compararVersiones(pv, dv) > 0) {
      desactualizados.push({ instancia: doc, plantilla })
    }
  }
  return desactualizados
}

/**
 * Agrupa documentos del cliente indicado por fase y por sección de la
 * Carpeta AFAC.
 */
export function agregarPorCliente(documentos, clienteId) {
  const docsCliente = documentos.filter((d) => {
    const c = d.cliente || d?.metadata?.cliente
    return c === clienteId
  })
  const porFase = {}
  const porSeccion = {}
  for (const d of docsCliente) {
    const fase = d.fase || d?.metadata?.fase
    if (fase) porFase[fase] = (porFase[fase] || 0) + 1
    const seccion = d.seccionAFAC ?? d?.metadata?.seccionAFAC
    if (seccion != null) {
      const key = `Sección ${String(seccion).padStart(2, '0')}`
      porSeccion[key] = (porSeccion[key] || 0) + 1
    }
  }
  return { porFase, porSeccion, total: docsCliente.length }
}
